import {
  Typography,
  Box,
  Grid,
  TextField,
  InputAdornment,
} from "@mui/material";
import StandardButton from "../../../../components/StandardButton";
import React from "react";
import { nanoid } from "nanoid";

import SupplementEditDialog from "./SupplementEditDialog";
import useSupplements from "../../../../hooks/useSupplements";
import MightyDataGrid from "../../../../components/organisms/MightyDataGrid";
import useEvents from "../../../../hooks/useEvents";
import EmptySupplements from "./EmptySupplements";

const EditSupplements = () => {
  const {
    supplements,
    supplementsLoading,
    editSupplementData,
    deleteSupplementData,
  } = useSupplements();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  const selectedSupplement = React.useRef();
  const events = useEvents();

  // Move this hook to the top level to avoid conditional hook calls
  const filteredSupplements = React.useMemo(
    () =>
      supplements?.filter((supplement) =>
        supplement.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ) || [],
    [supplements, searchTerm],
  );

  const columns = React.useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Name",
        minWidth: 100,
        flex: 1,
        hideable: false,
      },
      {
        field: "salesLink",
        headerName: "Sales URL",
        minWidth: 100,
        flex: 1,
        hideable: false,
      },
    ];
  }, []);

  const handleEdit = (supplement) => {
    selectedSupplement.current = supplement;
    setDialogOpen(true);
    events.logButtonClick({
      buttonName: "Edit Supplement",
      pageName: "Nutrition",
      tabName: "Supplements",
    });
  };

  const handleDelete = (supplement) => {
    deleteSupplementData(supplement.id);
    events.logButtonClick({
      buttonName: "Delete Supplement",
      pageName: "Nutrition",
      tabName: "Supplements",
    });
  };

  return (
    <>
      {/* Show loading first, then empty state when data confirms no supplements */}
      {supplementsLoading ? (
        <Typography>Loading supplements...</Typography>
      ) : !supplements || supplements.length === 0 ? (
        <EmptySupplements
          onAddSupplement={() => {
            selectedSupplement.current = null;
            setDialogOpen(true);
          }}
        />
      ) : (
        <>
          <Grid container style={{ marginTop: 0 }}>
            <Grid item xs={8}>
              <Typography variant="h6">Supplements</Typography>
              <Typography
                style={{ fontSize: 16, fontWeight: "normal", marginBottom: 12 }}
              >
                This your supplement database. Setup supplments you will
                prescribe to your clients in the Client Summary.
              </Typography>
            </Grid>

            <Grid item xs={4}>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent={"flex-end"}
              >
                <StandardButton
                  style={{ marginTop: 12 }}
                  variant="primary"
                  onClick={() => {
                    selectedSupplement.current = null;
                    setDialogOpen(true);
                    events.logButtonClick({
                      buttonName: "Add Supplement",
                      pageName: "Nutrition",
                      tabName: "Supplements",
                    });
                  }}
                >
                  Add Supplement
                </StandardButton>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <TextField
              placeholder="Search supplements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">🔍</InputAdornment>
                ),
              }}
            />
          </Box>
          <MightyDataGrid
            entityName="supplements"
            columns={columns}
            rows={filteredSupplements}
            showActions={true}
            actionConfig={{
              onEdit: handleEdit,
              onDelete: handleDelete,
              entityName: "Supplement",
              pageName: "Nutrition",
              tabName: "Supplements",
            }}
          />
        </>
      )}

      {/* Dialog - Always rendered regardless of empty state */}

      {dialogOpen && (
        <SupplementEditDialog
          open={dialogOpen}
          supplement={selectedSupplement.current}
          onDialogClosed={async (data, supplement) => {
            if (!data && !supplement) {
              setDialogOpen(false);
              return;
            }
            setDialogOpen(false);

            if (data && supplement) {
              supplement.name = data.name;
              supplement.description = data.description;
              supplement.salesLink = data.salesLink;
              supplement.updatedOn = new Date();

              editSupplementData(supplement);
            } else {
              const newSupplement = {
                id: nanoid(),
                name: data.name,
                description: data.description,
                salesLink: data.salesLink,
                createdOn: new Date(),
                updatedOn: new Date(),
              };

              editSupplementData(newSupplement);
            }
          }}
        />
      )}
    </>
  );
};

export default EditSupplements;
